// src/components/Auth/SignUp.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import googleIcon from "../../assets/images/others/google.svg";
import shapeIcon from "../../assets/images/contact/4.png";
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

const SignUp = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    password_confirmation: "",
    phone: "",
    telegram_chat_id: ""
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [zxcvbn, setZxcvbn] = useState(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Dynamically import zxcvbn for password strength
  useEffect(() => {
    const loadZxcvbn = async () => {
      try {
        const zxcvbnModule = await import('zxcvbn');
        setZxcvbn(() => zxcvbnModule.default || zxcvbnModule);
      } catch (error) {
        console.error("Failed to load zxcvbn:", error);
      }
    };
    loadZxcvbn();
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    // Update password strength in real-time
    if (id === 'password' && zxcvbn) {
      const result = zxcvbn(value);
      setPasswordStrength(result.score);
    }
    
    // Clear field error when user types
    if (errors[id]) {
      setErrors(prev => ({
        ...prev,
        [id]: null
      }));
    }
  };

  const togglePasswordVisibility = (fieldId) => {
    const input = document.getElementById(fieldId);
    if (input) {
      input.type = input.type === "password" ? "text" : "password";
    }
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlert(false);
  };

  const validatePassword = (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasLetter: /[a-zA-Z]/.test(password),
      hasMixedCase: /[a-z]/.test(password) && /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    return requirements;
  };

  const showAlert = (message, severity = "error") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setOpenAlert(true);
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0: return '#ff4444';
      case 1: return '#ffbb33';
      case 2: return '#ffdd59';
      case 3: return '#5cb85c';
      case 4: return '#00C851';
      default: return '#cccccc';
    }
  };

  const getPasswordStrengthLabel = () => {
    return ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'][passwordStrength] || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    
    // Client-side validation
    const passwordValidation = validatePassword(formData.password);
    const allRequirementsMet = Object.values(passwordValidation).every(Boolean);
    
    if (!allRequirementsMet) {
      showAlert("Password must contain: 8+ characters, uppercase, lowercase, number, and symbol");
      setIsSubmitting(false);
      return;
    }
    
    if (formData.password !== formData.password_confirmation) {
      showAlert("Passwords do not match");
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        phone: formData.phone || undefined,
        telegram_chat_id: formData.telegram_chat_id || undefined
      });

      console.log('📝 Registration result:', result);

      if (result.success) {
        // Check if verification is required
        if (result.requiresVerification) {
          // Store email for verification page
          localStorage.setItem('verificationEmail', formData.email);
          
          // Show success message
          if (result.emailSent) {
            showAlert("Registration successful! Please check your email to verify your account.", "success");
          } else {
            showAlert("Registration successful! However, we could not send the verification email. Please request a new one.", "warning");
          }
          
          // Redirect to verification required page
          setTimeout(() => {
            navigate('/verify-required', { 
              state: { 
                email: formData.email,
                message: result.emailSent 
                  ? 'Verification email sent! Please check your inbox.'
                  : 'Please request a new verification email.'
              } 
            });
          }, 1500);
          return;
        }
        
        showAlert("Registration successful! Redirecting to login...", "success");
        setTimeout(() => navigate('/login'), 2000);
      } else {
        // Check if we have field-specific errors
        if (result.errors) {
          setErrors(result.errors);
          // Show the first error as an alert
          const firstError = Object.values(result.errors)[0];
          if (firstError) {
            showAlert(firstError, "error");
          } else {
            showAlert(result.message || 'Registration failed');
          }
        } else {
          showAlert(result.message || 'Registration failed');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      showAlert('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <section className="account padding-top padding-bottom sec-bg-color2">
        <div className="container">
          <div className="account__wrapper" dataAos="fade-up" dataAosDuration="800">
            <div className="row g-4">
              <div className="col-12">
                <div className="account__content account__content--style1">
                  <div className="account__header">
                    <h2>Create Your Account</h2>
                    <p>
                      Hey there! Ready to join the party? We just need a few details
                      from you to get started. Let's do this!
                    </p>
                  </div>

                  <div className="account__social">
                    <button className="account__social-btn" type="button">
                      <span>
                        <img src={googleIcon} alt="Google icon" />
                      </span>
                      Continue with Google
                    </button>
                  </div>

                  <div className="account__divider account__divider--style1">
                    <span>or</span>
                  </div>

                  <form className="account__form needs-validation" noValidate onSubmit={handleSubmit}>
                    <div className="row g-4">
                      <div className="col-12 col-md-6">
                        <div>
                          <label htmlFor="first_name" className="form-label">
                            First Name
                          </label>
                          <input
                            className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                            type="text"
                            id="first_name"
                            placeholder="Ex. John"
                            value={formData.first_name}
                            onChange={handleChange}
                            maxLength="255"
                            required
                          />
                          {errors.first_name && (
                            <div className="invalid-feedback">{errors.first_name}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div>
                          <label htmlFor="last_name" className="form-label">
                            Last Name
                          </label>
                          <input
                            className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                            type="text"
                            id="last_name"
                            placeholder="Ex. Doe"
                            value={formData.last_name}
                            onChange={handleChange}
                            maxLength="255"
                            required
                          />
                          {errors.last_name && (
                            <div className="invalid-feedback">{errors.last_name}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <div>
                          <label htmlFor="email" className="form-label">
                            Email
                          </label>
                          <input
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            type="email"
                            id="email"
                            placeholder="Enter your email"
                            value={formData.email}
                            onChange={handleChange}
                            maxLength="255"
                            required
                          />
                          {errors.email && (
                            <div className="invalid-feedback">{errors.email}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <div>
                          <label htmlFor="phone" className="form-label">
                            Phone Number (Optional)
                          </label>
                          <input
                            className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                            type="tel"
                            id="phone"
                            placeholder="+1234567890"
                            value={formData.phone}
                            onChange={handleChange}
                          />
                          {errors.phone && (
                            <div className="invalid-feedback">{errors.phone}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <div>
                          <label htmlFor="telegram_chat_id" className="form-label">
                            Telegram Chat ID (Optional)
                          </label>
                          <input
                            className={`form-control ${errors.telegram_chat_id ? 'is-invalid' : ''}`}
                            type="text"
                            id="telegram_chat_id"
                            placeholder="Your Telegram Chat ID"
                            value={formData.telegram_chat_id}
                            onChange={handleChange}
                          />
                          {errors.telegram_chat_id && (
                            <div className="invalid-feedback">{errors.telegram_chat_id}</div>
                          )}
                          <small className="text-muted">
                            You can get this by messaging our Telegram bot.
                          </small>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-pass">
                          <label htmlFor="password" className="form-label">
                            Password
                            <button
                              type="button"
                              className="btn btn-link p-0 ms-2"
                              onClick={() => setShowPasswordRequirements(!showPasswordRequirements)}
                              style={{ fontSize: '0.8rem' }}
                            >
                              {showPasswordRequirements ? 'Hide' : 'Show'} requirements
                            </button>
                          </label>
                          
                          {showPasswordRequirements && (
                            <div className="alert alert-info p-2 mb-2" style={{ fontSize: '0.8rem' }}>
                              <strong>Password must contain:</strong>
                              <ul className="mb-0">
                                <li className={formData.password.length >= 8 ? 'text-success' : ''}>
                                  At least 8 characters
                                </li>
                                <li className={/[a-zA-Z]/.test(formData.password) ? 'text-success' : ''}>
                                  At least one letter
                                </li>
                                <li className={/[a-z]/.test(formData.password) && /[A-Z]/.test(formData.password) ? 'text-success' : ''}>
                                  Both uppercase and lowercase
                                </li>
                                <li className={/\d/.test(formData.password) ? 'text-success' : ''}>
                                  At least one number
                                </li>
                                <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-success' : ''}>
                                  At least one symbol
                                </li>
                              </ul>
                            </div>
                          )}
                          
                          <div className="input-group">
                            <input
                              className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                              type="password"
                              id="password"
                              placeholder="Password"
                              value={formData.password}
                              onChange={handleChange}
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => togglePasswordVisibility('password')}
                            >
                              <i className={`fa ${formData.password ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                          </div>
                          
                          {formData.password && (
                            <div className="mt-2">
                              <div className="progress" style={{ height: '5px' }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${(passwordStrength + 1) * 25}%`,
                                    backgroundColor: getPasswordStrengthColor()
                                  }}
                                ></div>
                              </div>
                              <small className="text-muted">
                                Password strength: {getPasswordStrengthLabel()}
                              </small>
                            </div>
                          )}
                          
                          {errors.password && (
                            <div className="invalid-feedback d-block">{errors.password}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-pass">
                          <label htmlFor="password_confirmation" className="form-label">
                            Confirm Password
                          </label>
                          <div className="input-group">
                            <input
                              className={`form-control ${errors.password_confirmation ? 'is-invalid' : ''}`}
                              type="password"
                              id="password_confirmation"
                              placeholder="Re-type password"
                              value={formData.password_confirmation}
                              onChange={handleChange}
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => togglePasswordVisibility('password_confirmation')}
                            >
                              <i className={`fa ${formData.password_confirmation ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                          </div>
                          {errors.password_confirmation && (
                            <div className="invalid-feedback d-block">{errors.password_confirmation}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="trk-btn trk-btn--border trk-btn--primary d-block mt-4"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Signing Up...
                        </>
                      ) : 'Sign Up'}
                    </button>
                  </form>

                  <div className="account__switch">
                    <p>
                      Already have an account? <Link to="/login">Login</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="account__shape">
          <span className="account__shape-item account__shape-item--1">
            <img src={shapeIcon} alt="Shape icon" />
          </span>
        </div>
      </section>
    </div>
  );
};

export default SignUp;